/// Airport "sigil" tile for Tower Home — mirrors iOS LevelSigilView. Layers:
/// accent wash gradient → range rings → faint runway fingerprint (baked in
/// sigilGeometry.generated) → derived code glyph → flag chip + badge.
import { sigilCode, flagEmoji, accentForLevel, isLiveLevel } from '../data/towerHome'
import { SIGIL_GEO } from '../data/sigilGeometry.generated'
import type { DifficultyName } from '../data/userProfile'

const MEDAL: Record<DifficultyName, string> = {
  Novice: '🥉',
  Trainee: '🥈',
  Licensed: '🥇',
  Master: '👑',
}

export function LevelSigil(props: {
  id: string
  size?: number
  completed?: DifficultyName | null
  free?: boolean
  locked?: boolean
}) {
  const { id, size = 112, completed = null, free = false, locked = false } = props
  const accent = accentForLevel(id)
  const code = sigilCode(id)
  const flag = flagEmoji(id)
  const live = isLiveLevel(id)
  const rw = SIGIL_GEO[id]?.rw ?? []
  const fontSize = code.length >= 4 ? 25 : 33
  const gid = `sg-${id}`

  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className="block rounded-2xl ring-1 ring-white/10"
        style={{ opacity: locked ? 0.5 : 1 }}
      >
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.55" />
            <stop offset="55%" stopColor={accent} stopOpacity="0.12" />
            <stop offset="100%" stopColor="#05070d" stopOpacity="0.95" />
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="100" height="100" rx="14" fill={`url(#${gid})`} />
        <g stroke={accent} strokeOpacity="0.18" fill="none">
          <circle cx="50" cy="50" r="33" />
          <circle cx="50" cy="50" r="49" />
        </g>
        <g strokeLinecap="round">
          {rw.map((s, i) => (
            <g key={i}>
              <line x1={s[0] * 100} y1={s[1] * 100} x2={s[2] * 100} y2={s[3] * 100} stroke={accent} strokeOpacity="0.5" strokeWidth="3.4" />
              <line x1={s[0] * 100} y1={s[1] * 100} x2={s[2] * 100} y2={s[3] * 100} stroke="#fff" strokeOpacity="0.6" strokeWidth="1.3" />
            </g>
          ))}
        </g>
        <text
          x="50"
          y="51"
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={fontSize}
          fontWeight="800"
          fill="#fff"
          stroke="#05070d"
          strokeWidth="3.5"
          strokeOpacity="0.6"
          paintOrder="stroke"
          style={{ fontFamily: 'ui-rounded, system-ui, sans-serif', letterSpacing: '0.5px' }}
        >
          {code}
        </text>
      </svg>

      {flag && (
        <span className="absolute left-1 top-1 rounded bg-black/40 px-1 text-[11px] leading-snug ring-1 ring-white/15">
          {flag}
        </span>
      )}
      <div className="absolute right-1 top-1 flex items-center gap-1">
        {live && (
          <span className="rounded bg-black/75 px-1 py-px text-[8px] font-bold tracking-wide text-white ring-1 ring-white/10">
            <span className="text-rose-400">●</span> LIVE
          </span>
        )}
        {completed && (
          <span title={`${completed} cleared`} className="text-[15px] leading-none drop-shadow">
            {MEDAL[completed]}
          </span>
        )}
        {!completed && !live && free && (
          <span className="rounded bg-cyan-500/90 px-1 py-px text-[8px] font-bold tracking-wide text-white">
            FREE
          </span>
        )}
      </div>
      {locked && <span className="absolute bottom-1 right-1 text-[13px] leading-none">🔒</span>}
    </div>
  )
}
